import React, {FC, memo} from "react";
import {Alert, Table, Tag} from "antd";
import {CheckOutlined} from "@ant-design/icons";
import {ApolloError} from "@apollo/client";
import moment, {Moment} from "moment";
import classNames from "classnames";
import {green, red, yellow} from "@ant-design/colors";
import {
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    LessonBookingPlannerPreviewQuery,
    LessonBookingPlannerRateStatus,
    LessonBookingPlannerUnitStatus,
} from "../../../generated/graphql";
import SummarySection from "../../../views/SummarySection/SummarySection";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";
import {renderAmount} from "../../../helpers/renderAmount";
import {renderDateAsWeekdayTag, renderDateFormatted, renderPaymentType} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import i18n from "../../../services/i18n";
import Notes from "./Notes";
import {renderIndex} from "./helper";
import css from "./LessonBookings.less";

// Vorschau „Neu“ (Superuser), Entwurf A: Heute und Neu stehen Zeile für Zeile nebeneinander (Einheiten je Monat,
// Raten), geändert wird rechts hervorgehoben, darunter: was sich ändert, was daraus folgt, was das Büro entscheidet.
// Nur lesend.

type PlannerData = LessonBookingPlannerPreviewQuery["lessonBookingPlannerPreview"];
type PlannerRate = PlannerData["rates"][number];

type MonthRow = {key: string; label: string; before: number; after: number; removed: Array<string>; added: Array<string>; moved: Array<string>};

type LessonRow = {key: string; index: number; startDateTime: Date | string; before: boolean; after: boolean; status?: LessonBookingPlannerUnitStatus; movedFrom?: string; unresolved?: boolean};
type FormLesson = {id?: string | null; startDateTime: Date | string; indexOfLessonInCourse?: number | null};

type PlannerPreviewProps = {
    data?: PlannerData;
    loading: boolean;
    error?: ApolloError;
    booking?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    lessonsOfCourse?: Array<FormLesson>; // Kurseinheiten aus dem Formular (nach der Änderung); ohne id = neu, Platzhalter wie im Planner
    getHolidayInfo?: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
};

const eur = (amount: number) => renderAmount({amount});
const date = (value: string | Date) => moment(value).format("DD.MM.YYYY");
const day = (value: string | Date) => moment(value).format("DD.MM.");
const changed = (a: number, b: number) => Math.abs(a - b) > 0.005;
const monthChanged = (row: MonthRow) => row.removed.length + row.added.length + row.moved.length > 0;
const changedCell = {backgroundColor: yellow[1], fontWeight: 600};
const boundRow = {color: "#999"};

const signed = (amount: number) => (amount < 0 ? `− ${eur(Math.abs(amount))}` : `+ ${eur(amount)}`);
const deltaText = (delta: number, unit: "units" | "eur") => {
    if (unit === "units" ? delta === 0 : !changed(delta, 0)) return null;
    const text = unit === "units" ? `${delta > 0 ? "+" : "−"}${Math.abs(delta)}` : signed(delta);

    return <span style={{color: delta < 0 ? red[6] : green[7], fontWeight: 600}}>{text}</span>;
};

// Heute zählt am alten Datum, Neu am neuen (verschobene Einheit: gleiche ID, anderer Tag → beide Monate mit Hinweis).
const groupByMonth = (units: PlannerData["units"]): Array<MonthRow> => {
    const rows = new Map<string, MonthRow>();
    const rowFor = (value: string | Date) => {
        const m = moment(value);
        const key = m.format("YYYY-MM");
        const row = rows.get(key) ?? {key, label: m.format("MMMM YYYY"), before: 0, after: 0, removed: [], added: [], moved: []};

        rows.set(key, row);

        return row;
    };

    units.forEach((unit) => {
        const before = rowFor(unit.lessonDate);
        const after = rowFor(unit.movedTo ?? unit.lessonDate);

        if (unit.status !== LessonBookingPlannerUnitStatus.Added) before.before += 1;
        if (unit.status !== LessonBookingPlannerUnitStatus.Removed) after.after += 1;
        if (unit.status === LessonBookingPlannerUnitStatus.Removed) before.removed.push(day(unit.lessonDate));
        if (unit.status === LessonBookingPlannerUnitStatus.Added) after.added.push(day(unit.lessonDate));
        if (unit.movedTo) {
            const text = `${day(unit.lessonDate)} → ${day(unit.movedTo)}`;

            before.moved.push(text);
            if (after !== before) after.moved.push(text);
        }
    });

    return Array.from(rows.values()).sort((a, b) => a.key.localeCompare(b.key));
};

const statusTag = (rate: PlannerRate) => {
    if (rate.status === LessonBookingPlannerRateStatus.Bound) {
        return <Tag>{`gebunden: ${rate.boundReason ?? ""}`}</Tag>;
    }
    if (rate.status === LessonBookingPlannerRateStatus.New) {
        return <Tag color="green">{"neu"}</Tag>;
    }

    return <Tag color="blue">{"offen"}</Tag>;
};

// Alle Kurseinheiten mit Haken „Heute“ und „Neu“; aus dem Kurs gelöschte Einheiten kommen aus den Planner-Units dazu.
export const lessonRows = (units: PlannerData["units"], lessonsOfCourse: Array<FormLesson>): Array<LessonRow> => {
    const unitsById = new Map(units.map((unit) => [unit.lessonId, unit]));
    const rows: Array<LessonRow> = lessonsOfCourse.map((lesson, index) => {
        const id = lesson.id ?? `new-${index}`;
        const unit = unitsById.get(id);

        unitsById.delete(id);

        return {
            key: id,
            index: lesson.indexOfLessonInCourse ?? index,
            startDateTime: lesson.startDateTime,
            before: !!unit && unit.status !== LessonBookingPlannerUnitStatus.Added,
            after: !!unit && unit.status !== LessonBookingPlannerUnitStatus.Removed,
            status: unit?.status,
            // Die Zeile trägt das neue Datum aus dem Formular; die Herkunft steht im Grund.
            movedFrom: unit?.movedTo ? unit.lessonDate : undefined,
        };
    });

    // Einheiten ohne Treffer im Formular: aus dem Kurs gelöscht (Removed) oder nach „Neu generieren“ mit alter ID als Kept (Zuordnung offen).
    unitsById.forEach((unit) =>
        rows.push({
            key: unit.lessonId,
            index: -1,
            startDateTime: unit.lessonDate,
            before: unit.status !== LessonBookingPlannerUnitStatus.Added,
            after: unit.status !== LessonBookingPlannerUnitStatus.Removed,
            status: unit.status,
            unresolved: unit.status === LessonBookingPlannerUnitStatus.Kept,
        }),
    );
    rows.sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
    // Aus dem Kurs gelöschte Einheiten haben keine Nummer mehr: Position in der sortierten Gesamtliste, nicht auf der Seite.
    rows.forEach((row, position) => {
        if (row.index < 0) row.index = position;
    });

    return rows;
};

const PlannerPreview: FC<PlannerPreviewProps> = ({data, loading, error, booking, lessonsOfCourse, getHolidayInfo}) => {
    if (error) {
        return (
            <div style={{padding: "1rem"}}>
                <Alert type="error" showIcon message="Vorschau (neues Modell) konnte nicht berechnet werden" description={error.message} />
            </div>
        );
    }
    if (loading || !data) {
        return (
            <div style={{height: "70%"}}>
                <LoadingContent content="Vorschau (neues Modell) wird berechnet..." />
            </div>
        );
    }

    const months = groupByMonth(data.units);
    const priceChanged = changed(data.priceAfter, data.priceBefore);
    const sumBefore = data.rates.reduce((s, r) => s + r.amountBefore, 0);
    const sumAfter = data.rates.reduce((s, r) => s + r.amountAfter, 0);
    const boundCount = data.rates.filter((r) => r.status === LessonBookingPlannerRateStatus.Bound).length;
    const hasUnitPrice = data.unitPrice !== null && data.unitPrice !== undefined;

    // Auch Ersatz im selben Monat (Ausfall + Zubuchung, Saldo 0) ist eine Änderung.
    const changes: Array<string> = data.regenerated
        ? ["Alle Einheiten wurden neu erzeugt: Zuordnung zur Buchung offen, Bestand bleibt stehen"]
        : months.filter(monthChanged).map((m) => {
              const delta = m.after - m.before;

              return `${m.label}: ${delta > 0 ? "+" : delta < 0 ? "−" : "±"}${Math.abs(delta)} Einheiten (${[...m.removed, ...m.added, ...m.moved].join(", ")})`;
          });

    changes.push(priceChanged ? `Preis ${eur(data.priceBefore)} → ${eur(data.priceAfter)}` : "Preis unverändert");
    if (changes.length === 1) changes.unshift("Keine Änderung an den Einheiten");

    const consequences: Array<string> = data.rates
        .filter((r) => r.status === LessonBookingPlannerRateStatus.New || changed(r.amountAfter, r.amountBefore))
        .map((r) =>
            r.status === LessonBookingPlannerRateStatus.New ? `Neue Rate ${date(r.paymentDue)}: ${eur(r.amountAfter)}` : `Rate ${date(r.paymentDue)}: ${eur(r.amountBefore)} → ${eur(r.amountAfter)}`,
        );

    if (data.credit > 0.005) consequences.push(`Guthaben ${eur(data.credit)}: kein offener Posten kann den Ausgleich tragen`);
    if (consequences.length === 0) consequences.push("Raten unverändert");
    if (boundCount > 0) consequences.push(`${boundCount} gebundene ${boundCount === 1 ? "Rate bleibt" : "Raten bleiben"}, wie sie ${boundCount === 1 ? "ist" : "sind"}`);

    // Ratensumme = Einheiten × Einheitenpreis ± Altbestand + Gebühren/Zusatzposten/Gutschriften + Guthaben (Planner-Invariante)
    const term = (amount: number, label: string) => (changed(amount, 0) ? ` ${amount < 0 ? "−" : "+"} ${eur(Math.abs(amount))} ${label}` : "");
    // Die Gleichung steht nur, wenn sie aufgeht: bei storniert, „neu erzeugt“ oder Ratensumme ≠ Preis bricht der Planner vorher ab.
    const formulaValue = hasUnitPrice ? data.bookedUnitsAfter * (data.unitPrice as number) + data.legacyGap + data.nonCoursePostsSum + data.credit : NaN;
    const sumFormula = !hasUnitPrice
        ? "Pauschalpreis pro Kurs"
        : data.canceled
        ? "unverändert"
        : changed(formulaValue, sumAfter)
        ? "siehe Hinweise"
        : `= ${data.bookedUnitsAfter} × ${eur(data.unitPrice as number)}${term(data.legacyGap, "„Altbestand“")}${term(data.nonCoursePostsSum, "Gebühren/Zusatzposten/Gutschriften")}${term(
              data.credit,
              "Guthaben",
          )}`;

    const monthColumns = [
        {title: "Monat", dataIndex: "label", key: "label"},
        {title: "Heute", dataIndex: "before", key: "before", align: "right" as const, width: 70},
        {title: "", key: "delta", align: "center" as const, width: 50, render: (_: unknown, row: MonthRow) => deltaText(row.after - row.before, "units")},
        {
            title: "Neu",
            dataIndex: "after",
            key: "after",
            align: "right" as const,
            width: 70,
            onCell: (row: MonthRow) => ({style: monthChanged(row) ? changedCell : {}}),
        },
        {
            title: "Grund",
            key: "reason",
            onCell: (row: MonthRow) => ({style: monthChanged(row) ? changedCell : {}}),
            render: (_: unknown, row: MonthRow) => (
                <>
                    {row.removed.length > 0 && <div style={{color: red[6]}}>{`Ausfall ${row.removed.join(", ")}`}</div>}
                    {row.added.length > 0 && <div style={{color: green[7]}}>{`Zubuchung ${row.added.join(", ")}`}</div>}
                    {row.moved.length > 0 && <div style={{color: "#666"}}>{`Verschoben ${row.moved.join(", ")}`}</div>}
                </>
            ),
        },
    ];

    const rateColumns = [
        {title: "Rate", dataIndex: "paymentDue", key: "due", render: (value: string) => date(value)},
        {title: "Status", key: "status", render: (_: unknown, rate: PlannerRate) => statusTag(rate)},
        {
            title: "Heute",
            dataIndex: "amountBefore",
            key: "before",
            align: "right" as const,
            render: (value: number, rate: PlannerRate) => (rate.status === LessonBookingPlannerRateStatus.New ? "–" : eur(value)),
        },
        {title: "", key: "delta", align: "center" as const, width: 90, render: (_: unknown, rate: PlannerRate) => deltaText(rate.amountAfter - rate.amountBefore, "eur")},
        {
            title: "Neu",
            dataIndex: "amountAfter",
            key: "after",
            align: "right" as const,
            onCell: (rate: PlannerRate) => ({style: changed(rate.amountAfter, rate.amountBefore) ? changedCell : {}}),
            render: (value: number) => eur(value),
        },
        {
            title: "Ausgleich",
            key: "adjustments",
            onCell: (rate: PlannerRate) => ({style: rate.adjustments.length > 0 ? changedCell : {}}),
            render: (_: unknown, rate: PlannerRate) =>
                rate.adjustments.length === 0 ? null : (
                    <>
                        {rate.adjustments.map((adjustment, index) => (
                            <div key={index} style={{color: adjustment.amount < 0 ? red[6] : green[7]}}>{`${adjustment.reason} ${signed(adjustment.amount)}`}</div>
                        ))}
                    </>
                ),
        },
    ];

    const lessons = lessonsOfCourse ? lessonRows(data.units, lessonsOfCourse) : [];
    const check = (value: boolean) => (value ? <CheckOutlined style={{color: green[7]}} /> : <span style={{color: "#bbb"}}>{"–"}</span>);
    const lessonColumns = [
        {
            title: "#",
            key: "index",
            align: "center" as const,
            width: 50,
            render: (_: unknown, row: LessonRow) => renderIndex(row.index, getHolidayInfo?.(moment(row.startDateTime))),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.weekday(),
            key: "weekday",
            width: 110,
            render: (_: unknown, row: LessonRow) => renderDateAsWeekdayTag({value: moment(row.startDateTime).toISOString(), style: {width: "100%", textAlign: "center", fontWeight: 600}}),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.date(),
            key: "date",
            render: (_: unknown, row: LessonRow) => renderDateFormatted(moment(row.startDateTime).toISOString(), "DD. MMM YYYY"),
        },
        {title: "Heute", key: "before", align: "center" as const, width: 70, render: (_: unknown, row: LessonRow) => check(row.before)},
        {
            title: "Neu",
            key: "after",
            align: "center" as const,
            width: 70,
            onCell: (row: LessonRow) => ({style: row.before !== row.after ? changedCell : {}}),
            render: (_: unknown, row: LessonRow) => check(row.after),
        },
        {
            title: "Grund",
            key: "reason",
            onCell: (row: LessonRow) => ({style: row.before !== row.after || row.movedFrom || row.unresolved ? changedCell : {}}),
            render: (_: unknown, row: LessonRow) =>
                row.unresolved ? (
                    <span style={{color: "#666"}}>{"Zuordnung offen"}</span>
                ) : row.status === LessonBookingPlannerUnitStatus.Removed ? (
                    <span style={{color: red[6]}}>{"Ausfall"}</span>
                ) : row.status === LessonBookingPlannerUnitStatus.Added ? (
                    <span style={{color: green[7]}}>{"Zubuchung"}</span>
                ) : row.movedFrom ? (
                    <span style={{color: "#666"}}>{`Verschoben von ${day(row.movedFrom)}`}</span>
                ) : null,
        },
    ];

    const kpi = (units: number, price: number, highlight: boolean) => (
        <div style={{display: "flex", gap: 24, padding: "6px 8px"}}>
            {booking && (
                <div>
                    <div style={{fontSize: "0.85em", color: "#888"}}>{"Zahlweise"}</div>
                    <div style={{fontSize: "1.3em", fontWeight: 600, padding: "0 4px"}}>{renderPaymentType(booking.paymentType)}</div>
                </div>
            )}
            <div>
                <div style={{fontSize: "0.85em", color: "#888"}}>{"Einheiten"}</div>
                <div style={{fontSize: "1.3em", fontWeight: 600, ...(highlight && units !== data.bookedUnitsBefore ? changedCell : {}), padding: "0 4px"}}>{units}</div>
            </div>
            <div>
                <div style={{fontSize: "0.85em", color: "#888"}}>{"Preis"}</div>
                <div style={{fontSize: "1.3em", fontWeight: 600, ...(highlight && priceChanged ? changedCell : {}), padding: "0 4px"}}>{eur(price)}</div>
            </div>
            {hasUnitPrice && (
                <div>
                    <div style={{fontSize: "0.85em", color: "#888"}}>{"Einheitenpreis"}</div>
                    <div style={{fontSize: "1.3em", fontWeight: 600, padding: "0 4px"}}>{eur(data.unitPrice as number)}</div>
                </div>
            )}
        </div>
    );

    const list = (items: Array<string>) => (
        <ul style={{margin: 0, paddingLeft: "1.25rem"}}>
            {items.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ul>
    );

    const decision = data.canceled ? (
        <Alert
            type="info"
            showIcon
            message="Buchung ist storniert"
            description={
                <>
                    {"Einheiten werden abgeglichen, Preis und Raten bleiben unverändert."}
                    {data.warnings.length > 0 && list(data.warnings)}
                </>
            }
        />
    ) : data.warnings.length === 0 ? (
        <Alert type="success" showIcon message="Nichts zu entscheiden" description="Buchung konsistent: Preis = Einheiten × Einheitenpreis, Raten decken den Preis." />
    ) : (
        <Alert type="warning" showIcon message="Was das Büro entscheiden muss" description={list(data.warnings)} />
    );

    return (
        <div className={css.bookingRoot}>
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid lightgrey", marginBottom: 8}}>
                {kpi(data.bookedUnitsBefore, data.priceBefore, false)}
                {kpi(data.bookedUnitsAfter, data.priceAfter, true)}
            </div>
            <SummarySection title="Kurseinheiten je Monat" content={<Table<MonthRow> size="small" pagination={false} rowKey="key" dataSource={months} columns={monthColumns} />} />
            {lessonsOfCourse && (
                <SummarySection
                    title={i18n.containers.bookings.BookingSummary.sectionHeaders.courseLessons()}
                    style={{marginTop: 12}}
                    content={
                        <Table<LessonRow>
                            className="lessonSelectionTable"
                            size="small"
                            rowKey="key"
                            dataSource={lessons}
                            columns={lessonColumns}
                            pagination={{pageSize: 12, size: "small", hideOnSinglePage: true, showSizeChanger: false}}
                            rowClassName={(row) =>
                                classNames({[css.canceledBooking]: row.status === LessonBookingPlannerUnitStatus.Removed, [css.newBooking]: row.status === LessonBookingPlannerUnitStatus.Added})
                            }
                        />
                    }
                />
            )}
            <SummarySection
                title="Raten"
                style={{marginTop: 12}}
                content={
                    <Table<PlannerRate>
                        size="small"
                        pagination={false}
                        rowKey={(rate) => rate.id ?? `new-${String(rate.paymentDue)}`}
                        dataSource={data.rates}
                        columns={rateColumns}
                        onRow={(rate) => ({style: rate.status === LessonBookingPlannerRateStatus.Bound ? boundRow : {}})}
                        summary={() => (
                            <Table.Summary.Row style={{fontWeight: 600}}>
                                <Table.Summary.Cell index={0} colSpan={2}>
                                    {"Summe"}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    {eur(sumBefore)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="center">
                                    {deltaText(sumAfter - sumBefore, "eur")}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right">
                                    {eur(sumAfter)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>
                                    <span style={{fontWeight: 400, color: "#666"}}>{sumFormula}</span>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        )}
                    />
                }
            />
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12}}>
                <Alert type="info" message="Was sich ändert" description={list(changes)} />
                <Alert type="info" message="Was daraus folgt" description={list(consequences)} />
                {decision}
            </div>
            {booking && <SummarySection style={{marginTop: 12}} title={i18n.containers.bookings.BookingSummary.sectionHeaders.notes()} content={<Notes booking={booking} />} />}
        </div>
    );
};

export default memo(PlannerPreview);
